import { useEffect, useState } from 'react'
import { api } from '../services/api'
import { useStore } from '../store/useStore'

/**
 * storeId → 店舗名。**足りないものだけ自分で取りに行く。**
 *
 * zustand の `stores` は **Home の loadStores でしか埋まらない**。予約一覧へ
 * HOME を経由せずに来ると (AR の返却画面から / URL 直打ち / リロード後) 空のままで、
 * 店舗名が **ID のまま**表示されていた。同じ画面が、直前にどこを開いたかで
 * 見え方が変わるという分かりにくい挙動になっていた。
 *
 * 表示に要るものは、その画面が自分で揃える。既に持っているものは取りに行かない。
 */
export function useStoreNames(storeIds: string[]): (storeId: string) => string {
  const { stores } = useStore()
  const [fetched, setFetched] = useState<Record<string, string>>({})

  const key = [...new Set(storeIds)].sort().join(',')

  useEffect(() => {
    const missing = [...new Set(storeIds)].filter(
      (id) => id && !stores.some((s) => s.id === id) && !(id in fetched),
    )
    if (missing.length === 0) return
    let cancelled = false
    // **取れなくても表示は続ける** (名前が ID のままになるだけ)。ここで
    // 画面を止めると、予約そのものが見られなくなる。
    Promise.all(
      missing.map(async (id) => {
        try {
          const res = await api.getStore(id)
          const name = (res?.store?.name ?? res?.name) as string | undefined
          return name ? ([id, name] as const) : null
        } catch {
          return null
        }
      }),
    ).then((pairs) => {
      if (cancelled) return
      const add = Object.fromEntries(pairs.filter(Boolean) as (readonly [string, string])[])
      if (Object.keys(add).length > 0) setFetched((prev) => ({ ...prev, ...add }))
    })
    return () => { cancelled = true }
    // key で「必要な id の集合」が変わった時だけ走らせる (毎レンダー走らせない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, stores])

  return (storeId: string) =>
    stores.find((s) => s.id === storeId)?.name || fetched[storeId] || storeId
}
